import { BottomSheet } from '@bonanza-social/navigation';
import { useTranslation } from '@bonanza-social/i18n';
import {
  selectMetaGamesCountDataState,
  useInstantMetaGame,
} from '@bonanza-social/meta-games';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectIsNewLoyalty, selectStatus } from '@bonanza-social/status';
import { useCallback, useMemo } from 'react';
import {
  selectRemainingSpinsCount,
  selectRemainingSpinsCountState,
} from '@bonanza-social/instant-wheel';
import { PendingReward } from '../shared/pendingRewards.types';

const usePendingRewards = () => {
  const [t] = useTranslation('rewards');
  const { launchMetaGame: launchWheel, metaGameCount: wheelCount } =
    useInstantMetaGame('Wheel');
  const { launchMetaGame: launchScratcher, metaGameCount: scratcherCount } =
    useInstantMetaGame('Scratcher');
  const metaGamesCountDataState = useSelector(selectMetaGamesCountDataState);
  const status = useSelector(selectStatus);
  const instantWheelCount = useSelector(selectRemainingSpinsCount);
  const instantWheelCountDataState = useSelector(
    selectRemainingSpinsCountState,
  );
  const isNewLoyalty = useSelector(selectIsNewLoyalty);

  const pendingRewardsLoading = useMemo(
    () =>
      metaGamesCountDataState === 'idle' ||
      metaGamesCountDataState === 'pending' ||
      instantWheelCountDataState === 'idle' ||
      instantWheelCountDataState === 'pending',
    [metaGamesCountDataState, instantWheelCountDataState],
  );

  const launchInstantWheel = useCallback(() => {
    if (status?.playerStatus) {
      BottomSheet.open({
        namespace: 'instantWheel',
        type: 'InstantWheel',
        borderGradient: `status${status.playerStatus}Background`,
        // TODO: Remove if condition when New Loyalty is released
        headerImageName: isNewLoyalty
          ? `status.${status.playerStatus}Gem`
          : undefined,
        borderStyle: 'allsides',
      });
    }
  }, [status]);

  const pendingRewards = useMemo(() => {
    const pendingRewards: PendingReward[] = [];

    if (scratcherCount) {
      pendingRewards.push({
        imageName: 'common.BonanzaScratchcard',
        onPress: launchScratcher,
        ctaLocKey: 'scratchNow',
        rewardText: t('scratchcardsCount', {
          count: scratcherCount,
        }),
      });
    }

    if (wheelCount) {
      pendingRewards.push({
        imageName: 'common.BonanzaWheel',
        onPress: launchWheel,
        ctaLocKey: 'spinNow',
        rewardText: t('wheelSpinsCount', {
          count: wheelCount,
        }),
      });
    }

    if (instantWheelCount) {
      pendingRewards.push({
        imageName: 'common.BonanzaWheel',
        onPress: launchInstantWheel,
        ctaLocKey: 'spinNow',
        rewardText: t('instantWheelSpinsCount', {
          count: instantWheelCount,
        }),
      });
    }

    return pendingRewards;
  }, [
    instantWheelCount,
    launchInstantWheel,
    launchScratcher,
    launchWheel,
    scratcherCount,
    t,
    wheelCount,
  ]);

  return { pendingRewards, pendingRewardsLoading };
};

export default usePendingRewards;
